import React, { useMemo, useState, useEffect, useCallback } from "react";
import SearchFlightsNumber from "./SearchFlightNumber";
import SearchByRoute from "./SearchByRoute";
import { getRecentRequests } from "apis/myRequestsService";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import {
  MyRequestParams,
  MyRequestResponse,
} from "@pages/YourRequest/types";
import FlightListing from "./LoadsFlightListing";
import { useCreditStore } from "store/creditStore";

interface LoadsProps {
  readOnly?: boolean;
  breadcrumbLabel?: string;
}

const Loads: React.FC<LoadsProps> = ({
  readOnly = false,
  breadcrumbLabel = "Loads",
}) => {
  const { showLoader, hideLoader } = useGlobalLoader();

  const [activeTab, setActiveTab] = useState<"flightNumber" | "route">(
    "route",
  );

  const [flights, setFlights] = useState<MyRequestResponse>({
    items: [],
    totalCount: 0,
    page: 1,
    pageSize: 5,
  });

  const [loading, setLoading] = useState<boolean>(true);
  const [showSearchResults, setShowSearchResults] =
    useState<boolean>(false);

  // Voice heard a flight number while the user was on the Route tab — carry
  // it across the tab switch since SearchFlightsNumber mounts fresh.
  const [voiceFlightPrefill, setVoiceFlightPrefill] = useState<{
    flightNumber: string;
    nonce: number;
  } | null>(null);

  // Only the retired tab buttons called this.
  // const handleTabSwitch = (tab: "flightNumber" | "route") => {
  //   setActiveTab(tab);
  //   setShowSearchResults(false);
  // };

  // Retired — SearchByRoute fills a heard flight number into its own field.
  // const handleVoiceFlightNumberDetected = useCallback((flightNumber: string) => {
  //   setVoiceFlightPrefill({ flightNumber, nonce: Date.now() });
  //   setActiveTab("flightNumber");
  //   setShowSearchResults(false);
  // }, []);

  const handleSearchResultsShow = (show: boolean) => {
    setShowSearchResults(show);
  };

  const fetchRecentFlights = useCallback(async (silent = false) => {
    if (!silent) {
      showLoader();
      setLoading(true);
    }

    try {
      const params: MyRequestParams = {
        page: 1,
        size: 5,
        sortBy: "createdDate",
        sortOrder: "desc",
      };

      const data = await getRecentRequests(params);
      setFlights(data);
    } catch (err) {
      console.error("Error fetching recent flights:", err);
    } finally {
      if (!silent) {
        setLoading(false);
        setTimeout(() => hideLoader(), 500);
      }
    }
  }, []);

  // Fetch only once when the component mounts
  useEffect(() => {
    fetchRecentFlights();
    // Tokens-per-request is read once here and served from the store to the
    // search results banner, so it isn't refetched on every search.
    useCreditStore.getState().refreshRequestCost();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After a successful post
  const handlePostedRequests = useCallback(
    (optimisticItems: any[] = [], options?: { keepResults?: boolean }) => {
      if (optimisticItems.length > 0) {
        setFlights((prev: any) => {
          const existing = Array.isArray(prev)
            ? prev
            : Array.isArray(prev?.items)
              ? prev.items
              : [];

          return [...optimisticItems, ...existing].slice(0, 5) as any;
        });
      }

      // Posting from a single flight card keeps the user on the search results.
      if (!options?.keepResults) {
        setShowSearchResults(false);
      }

      // Silent refresh after posting
      fetchRecentFlights(true);
    },
    [fetchRecentFlights],
  );

  // Tab buttons — retired. The route search bar now carries its own optional
  // flight number field, and SearchByRoute never rendered these, so the flight
  // tab was unreachable by click. SearchFlightNumber is still reached from
  // voice via handleVoiceFlightNumberDetected.
  // const tabButtons = (
  //   <div className="inline-flex items-center bg-[#181A1D] py-1 px-[9px] rounded-md border border-white/10 shadow-inner h-10">
  //     {/* Route Tab */}
  //     <button
  //       role="tab"
  //       aria-selected={activeTab === "route"}
  //       aria-controls="route-panel"
  //       className={`flex items-center gap-2 px-3.5 h-8 text-xs font-medium font-inter rounded-full transition-all duration-200 ${
  //         activeTab === "route"
  //           ? "bg-[#FFBC00] text-black shadow-md"
  //           : "text-white hover:text-gray-200 hover:bg-white/5"
  //       }`}
  //       onClick={() => handleTabSwitch("route")}
  //       type="button"
  //     >
  //       <i className="st-route text-sm rotate-90 inline-block"></i>
  //       <span>Route Search</span>
  //     </button>
  //
  //     {/* Flight Number Tab */}
  //     <button
  //       role="tab"
  //       aria-selected={activeTab === "flightNumber"}
  //       aria-controls="flight-number-panel"
  //       className={`flex items-center gap-2 px-3.5 h-8 text-xs font-medium font-manrope rounded-full transition-all duration-200 ${
  //         activeTab === "flightNumber"
  //           ? "bg-[#FFBC00] text-black shadow-md"
  //           : "text-white hover:text-gray-200 hover:bg-white/5"
  //       }`}
  //       onClick={() => handleTabSwitch("flightNumber")}
  //       type="button"
  //     >
  //       <i
  //         className="st-flight text-sm inline-block"
  //         style={{ transform: "rotate(-50deg)" }}
  //       ></i>
  //       <span>Flight No.</span>
  //     </button>
  //   </div>
  // );

  const activeContent = useMemo(
    () =>
      activeTab === "flightNumber" ? (
        <section>
          <SearchFlightsNumber
            onShowSearchResults={handleSearchResultsShow}
            onPostRequestSuccess={handlePostedRequests}
            // tabButtons={tabButtons}
            onBack={() => setShowSearchResults(false)}
            readOnly={readOnly}
            breadcrumbLabel={breadcrumbLabel}
            voicePrefill={voiceFlightPrefill}
          />
        </section>
      ) : (
        <section>
          <SearchByRoute
            onShowSearchResults={handleSearchResultsShow}
            onPostRequestSuccess={handlePostedRequests}
            // tabButtons={tabButtons}
            onBack={() => setShowSearchResults(false)}
            readOnly={readOnly}
            breadcrumbLabel={breadcrumbLabel}
            // onVoiceFlightNumberDetected={handleVoiceFlightNumberDetected}
          />
        </section>
      ),
    [
      activeTab,
      handlePostedRequests,
      readOnly,
      breadcrumbLabel,
      voiceFlightPrefill,
      // handleVoiceFlightNumberDetected,
    ],
  );

  return (
    <div className="relative flex-1 flex flex-col">
      {/* Search directly at the top */}
      <div className="px-2 pt-2 ">
        {activeContent}
      </div>

      {/* Recently Posted Requests */}
      {!showSearchResults && !readOnly && (
        <div className="relative z-10 px-2 mt-2">
          <FlightListing
            flights={flights}
            show={showSearchResults}
            FlightLoading={loading}
          />
        </div>
      )}
    </div>
  );
};

export default Loads;